import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Schedule } from "@okeys/shared-utils"
import { PlusIcon, TrashIcon } from "@radix-ui/react-icons"
import React, { useEffect, useRef, useState } from "react"

type ScheduleEditorProps = {
  initialSchedule?: Schedule
  onScheduleChange: (schedule: Schedule) => void
  className?: string
}

type DayOfWeek = keyof Schedule

const DAYS_CONFIG: { key: DayOfWeek; label: string }[] = [
  { key: "monday", label: "Lundi" },
  { key: "tuesday", label: "Mardi" },
  { key: "wednesday", label: "Mercredi" },
  { key: "thursday", label: "Jeudi" },
  { key: "friday", label: "Vendredi" },
  { key: "saturday", label: "Samedi" },
  { key: "sunday", label: "Dimanche" },
]

const DEFAULT_SCHEDULE: Schedule = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
}

const MAX_SLOTS_PER_DAY = 2

export const ScheduleEditor: React.FC<ScheduleEditorProps> = ({
  initialSchedule,
  onScheduleChange,
  className = "",
}) => {
  const [schedule, setSchedule] = useState<Schedule>(DEFAULT_SCHEDULE)
  const [dayEnabled, setDayEnabled] = useState<Record<DayOfWeek, boolean>>({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  })

  // Utiliser une ref pour éviter les appels en boucle
  const lastNotifiedSchedule = useRef<Schedule | null>(null)
  const isInitialized = useRef(false)

  // Initialiser une seule fois avec les horaires existants
  useEffect(() => {
    if (!isInitialized.current) {
      if (initialSchedule) {
        setSchedule(initialSchedule)
        // Déterminer quels jours sont activés
        const enabled: Record<DayOfWeek, boolean> = {} as Record<
          DayOfWeek,
          boolean
        >
        DAYS_CONFIG.forEach(({ key }) => {
          enabled[key] = initialSchedule[key].length > 0
        })
        setDayEnabled(enabled)
        lastNotifiedSchedule.current = initialSchedule
      } else {
        setSchedule(DEFAULT_SCHEDULE)
        setDayEnabled({
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false,
        })
        lastNotifiedSchedule.current = DEFAULT_SCHEDULE
      }
      isInitialized.current = true
    }
  }, [initialSchedule])

  // Notifier les changements seulement si le schedule a vraiment changé
  useEffect(() => {
    if (isInitialized.current) {
      const scheduleString = JSON.stringify(schedule)
      const lastScheduleString = JSON.stringify(lastNotifiedSchedule.current)

      if (scheduleString !== lastScheduleString) {
        lastNotifiedSchedule.current = schedule
        onScheduleChange(schedule)
      }
    }
  }, [schedule, onScheduleChange])

  const handleDayToggle = (day: DayOfWeek, enabled: boolean) => {
    setDayEnabled((prev) => ({ ...prev, [day]: enabled }))

    if (enabled) {
      // Ajouter un créneau par défaut
      setSchedule((prev) => ({
        ...prev,
        [day]: [{ start: "09:00", end: "17:00" }],
      }))
    } else {
      // Vider les créneaux
      setSchedule((prev) => ({
        ...prev,
        [day]: [],
      }))
    }
  }

  const addTimeSlot = (day: DayOfWeek) => {
    setSchedule((prev) => {
      // Vérifier si on a déjà atteint la limite
      if (prev[day].length >= MAX_SLOTS_PER_DAY) {
        return prev
      }

      const lastSlot = prev[day][prev[day].length - 1]
      const newStart = lastSlot ? lastSlot.end : "09:00"
      const newEnd = lastSlot ? addHours(lastSlot.end, 1) : "17:00"

      return {
        ...prev,
        [day]: [...prev[day], { start: newStart, end: newEnd }],
      }
    })
  }

  const removeTimeSlot = (day: DayOfWeek, index: number) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index),
    }))
  }

  const updateTimeSlot = (
    day: DayOfWeek,
    index: number,
    field: "start" | "end",
    value: string
  ) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: prev[day].map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot
      ),
    }))
  }

  const addHours = (time: string, hours: number): string => {
    const [h, m] = time.split(":").map(Number)
    const newHour = (h + hours) % 24
    return `${newHour.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}`
  }

  const getTotalHours = (): number => {
    let total = 0
    DAYS_CONFIG.forEach(({ key }) => {
      if (dayEnabled[key]) {
        schedule[key].forEach((slot) => {
          const [startH, startM] = slot.start.split(":").map(Number)
          const [endH, endM] = slot.end.split(":").map(Number)
          const startMinutes = startH * 60 + startM
          const endMinutes = endH * 60 + endM
          total += (endMinutes - startMinutes) / 60
        })
      }
    })
    return total
  }

  const validateSchedule = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = []

    for (const day of DAYS_CONFIG) {
      if (dayEnabled[day.key]) {
        const daySchedule = schedule[day.key]

        // Vérifier qu'il y a au moins un créneau
        if (daySchedule.length === 0) {
          errors.push(`${day.label}: Aucun créneau défini`)
          continue
        }

        // Vérifier que chaque créneau est valide
        for (let i = 0; i < daySchedule.length; i++) {
          const slot = daySchedule[i]
          if (!slot.start || !slot.end) {
            errors.push(`${day.label}: Créneau ${i + 1} incomplet`)
          } else if (slot.start >= slot.end) {
            errors.push(
              `${day.label}: Créneau ${
                i + 1
              } - L'heure de fin doit être après l'heure de début`
            )
          }
        }

        // Vérifier qu'il n'y a pas de chevauchements
        const sortedSlots = [...daySchedule].sort((a, b) =>
          a.start.localeCompare(b.start)
        )
        for (let i = 0; i < sortedSlots.length - 1; i++) {
          if (sortedSlots[i].end > sortedSlots[i + 1].start) {
            errors.push(`${day.label}: Créneaux qui se chevauchent`)
          }
        }
      }
    }

    return { isValid: errors.length === 0, errors }
  }

  const validation = validateSchedule()

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Résumé */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total heures/semaine
              </p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {getTotalHours().toFixed(1)}h
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Jours travaillés
              </p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {Object.values(dayEnabled).filter(Boolean).length}/7
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Statut
              </p>
              <p
                className={`text-sm font-medium ${
                  validation.isValid
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {validation.isValid ? "✓ Valide" : "⚠ Erreurs"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Erreurs de validation */}
      {!validation.isValid && (
        <Card className="border-red-200 dark:border-red-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-red-600 dark:text-red-400 text-base">
              Erreurs de validation
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="list-disc list-inside space-y-1 text-sm text-red-600 dark:text-red-400">
              {validation.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Horaires par jour */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DAYS_CONFIG.map(({ key, label }) => (
          <Card
            key={key}
            className={
              dayEnabled[key] ? "border-blue-200 dark:border-blue-700" : ""
            }
          >
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between text-base">
                <span>{label}</span>
                <Switch
                  checked={dayEnabled[key]}
                  onCheckedChange={(checked) => handleDayToggle(key, checked)}
                />
              </CardTitle>
            </CardHeader>

            {dayEnabled[key] && (
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {schedule[key].map((slot, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="flex-1">
                        <Label className="text-xs">Début</Label>
                        <Input
                          type="time"
                          value={slot.start}
                          onChange={(e) =>
                            updateTimeSlot(key, index, "start", e.target.value)
                          }
                          className="text-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs">Fin</Label>
                        <Input
                          type="time"
                          value={slot.end}
                          onChange={(e) =>
                            updateTimeSlot(key, index, "end", e.target.value)
                          }
                          className="text-sm"
                        />
                      </div>
                      {schedule[key].length > 1 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeTimeSlot(key, index)}
                          className="mt-5 text-red-500 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  {schedule[key].length < MAX_SLOTS_PER_DAY && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addTimeSlot(key)}
                      className="w-full text-blue-500 hover:text-blue-600"
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Ajouter un créneau{" "}
                      {schedule[key].length > 0 &&
                        `(${schedule[key].length}/${MAX_SLOTS_PER_DAY})`}
                    </Button>
                  )}

                  {schedule[key].length >= MAX_SLOTS_PER_DAY && (
                    <div className="w-full p-2 text-center text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded border border-dashed">
                      Maximum de {MAX_SLOTS_PER_DAY} créneaux par jour atteint
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  )
}
